/**
 * Transcript types.
 *
 * A `TranscriptEntry` is the durable unit of a conversation. The in-memory
 * rolling context window keeps only the last `MAX_CONTEXT_TURNS` of these;
 * the full transcript lives on the client, and is optionally mirrored to R2.
 */

import type { LanguageCode } from "./language.js";

/** How final an entry is. Entries are appended as `partial` then upgraded. */
export type EntryStatus = "partial" | "final" | "failed";

export interface TranscriptEntry {
  /** Stable id, also used as the subtitle cue id. */
  readonly id: string;
  /** Monotonic index within the session; 0-based. */
  readonly index: number;
  /** `HH:MM:SS.mmm` — wall clock time the segment opened. */
  readonly timestamp: string;
  /** Epoch ms the segment opened. */
  readonly startedAt: number;
  /** Epoch ms the segment was finalized. */
  readonly endedAt: number;
  /** Offset from session start, for SRT/VTT cues. */
  readonly startMs: number;
  /** Offset from session start, for SRT/VTT cues. */
  readonly endMs: number;
  readonly speaker: string;
  readonly sourceLanguage: LanguageCode;
  readonly sourceText: string;
  readonly targetLanguage: LanguageCode;
  readonly translatedText: string;
  readonly detectionConfidence: number | null;
  readonly status: EntryStatus;
  /** Which ASR provider produced `sourceText`. */
  readonly transcriptionProvider: string;
  /** Which translation provider produced `translatedText`. */
  readonly translationProvider: string;
  readonly transcriptionMs: number;
  readonly translationMs: number;
}

export interface Transcript {
  readonly sessionId: string;
  readonly createdAt: number;
  readonly updatedAt: number;
  readonly sourceLanguage: LanguageCode;
  readonly targetLanguage: LanguageCode;
  readonly entries: readonly TranscriptEntry[];
  /** Set when the transcript was mirrored to R2. */
  readonly transcriptId: string | null;
  readonly version: 1;
}

export const EMPTY_TRANSCRIPT: Transcript = {
  sessionId: "",
  createdAt: 0,
  updatedAt: 0,
  sourceLanguage: "auto",
  targetLanguage: "en",
  entries: [],
  transcriptId: null,
  version: 1,
};

export type TranscriptExportFormat = "txt" | "json" | "srt" | "vtt" | "md";

// ---------------------------------------------------------------------------
//  Formatting helpers — shared so client and exporter never disagree.
// ---------------------------------------------------------------------------

/** `HH:MM:SS.mmm` */
export function formatClock(epochMs: number): string {
  return formatOffset(epochMs);
}

/** `HH:MM:SS.mmm` from a millisecond offset. */
export function formatOffset(offsetMs: number): string {
  const safe = Math.max(0, Math.floor(offsetMs));
  const hours = Math.floor(safe / 3_600_000);
  const minutes = Math.floor((safe % 3_600_000) / 60_000);
  const seconds = Math.floor((safe % 60_000) / 1000);
  const millis = safe % 1000;
  return `${pad(hours, 2)}:${pad(minutes, 2)}:${pad(seconds, 2)}.${pad(millis, 3)}`;
}

/** `00:01:23,456` — SRT cue timestamp. */
export function formatSrtTimestamp(offsetMs: number): string {
  const safe = Math.max(0, Math.floor(offsetMs));
  const hours = Math.floor(safe / 3_600_000);
  const minutes = Math.floor((safe % 3_600_000) / 60_000);
  const seconds = Math.floor((safe % 60_000) / 1000);
  const millis = safe % 1000;
  return `${pad(hours, 2)}:${pad(minutes, 2)}:${pad(seconds, 2)},${pad(millis, 3)}`;
}

/** `00:01:23.456` — WebVTT cue timestamp. */
export function formatVttTimestamp(offsetMs: number): string {
  return formatSrtTimestamp(offsetMs).replace(",", ".");
}

/** `12:41:03` — the compact wall-clock form used in the transcript list. */
export function formatShortClock(epochMs: number): string {
  const date = new Date(epochMs);
  return `${pad(date.getHours(), 2)}:${pad(date.getMinutes(), 2)}:${pad(date.getSeconds(), 2)}`;
}

function pad(value: number, width: number): string {
  return String(value).padStart(width, "0");
}
